import React, { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ShieldCheck, Camera, ArrowRight, Loader2, CheckCircle2, AlertCircle, Smartphone, RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

/**
 * ParentVerificationModal — the progressive-verification gate (Family Safety
 * Check). STRICTLY LIVE CAMERA ONLY: file uploads are blocked to prevent kids
 * from submitting a pre-saved photo from the camera roll or hard drive.
 *
 * • The only capture mechanism is `navigator.mediaDevices.getUserMedia` with
 *   `facingMode: "user"` (front camera on mobile, webcam on desktop). The
 *   frame is drawn to a canvas and `toDataURL('image/jpeg')`'d — no file input
 *   exists anywhere in this component.
 * • If the browser refuses camera access, we surface a helper that asks the
 *   parent to complete the check on a mobile phone or tablet instead.
 *
 * Props:
 *   open, onOpenChange   — Dialog control
 *   onVerified()         — called after a successful save-identity + refresh
 *   reason (optional)    — small copy line, e.g. "before you list this toy"
 */
export default function ParentVerificationModal({ open, onOpenChange, onVerified, reason }) {
  const { refresh } = useAuth();
  const [dob, setDob] = useState("");
  // DOB dropdowns — parents choose day/month/year separately so they don't
  // have to page through 40+ years on a mobile native date-picker.
  const [dobYear, setDobYear] = useState("");
  const [dobMonth, setDobMonth] = useState("");
  const [dobDay, setDobDay] = useState("");
  const [selfie, setSelfie] = useState(""); // captured frame data-URL
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [err, setErr] = useState("");
  // Camera lifecycle: probing → stream ready → capturing → blocked | unsupported
  const [camState, setCamState] = useState("idle"); // idle | requesting | ready | blocked | unsupported
  const [camError, setCamError] = useState("");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  // Detach the camera stream when the modal closes or the component unmounts.
  const stopCamera = useCallback(() => {
    const s = streamRef.current;
    if (s) {
      try { s.getTracks().forEach((t) => t.stop()); } catch (_) { /* noop */ }
      streamRef.current = null;
    }
    if (videoRef.current) { try { videoRef.current.srcObject = null; } catch (_) { /* noop */ } }
  }, []);

  const startCamera = useCallback(async () => {
    setCamError("");
    if (typeof navigator === "undefined"
        || !navigator.mediaDevices
        || typeof navigator.mediaDevices.getUserMedia !== "function") {
      setCamState("unsupported");
      return;
    }
    setCamState("requesting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        // Explicitly request the FRONT camera on mobile ("user" facing mode)
        // and the default webcam on desktop. Audio never captured.
        video: { facingMode: { ideal: "user" }, width: { ideal: 720 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // iOS Safari requires the video element to be playsInline + muted
        // before the stream will render inside a modal.
        videoRef.current.muted = true;
        videoRef.current.playsInline = true;
        await videoRef.current.play().catch(() => { /* autoplay may already be running */ });
      }
      setCamState("ready");
    } catch (ex) {
      // NotAllowedError (denied) / NotFoundError (no camera) / SecurityError
      // (insecure origin) / OverconstrainedError all land here. We treat all
      // of them as "blocked" and route the parent to their phone.
      const name = ex?.name || "";
      setCamError(name || String(ex));
      setCamState(name === "NotFoundError" || name === "OverconstrainedError" ? "unsupported" : "blocked");
    }
  }, []);

  // Start the camera automatically the moment the modal opens; tear it down on
  // close or unmount so the LED / permissions indicator doesn't linger.
  useEffect(() => {
    if (open) {
      setStatus("idle"); setErr(""); setDob(""); setSelfie("");
      setDobYear(""); setDobMonth(""); setDobDay("");
      startCamera();
    } else {
      stopCamera();
    }
    return () => stopCamera();
  }, [open, startCamera, stopCamera]);

  // Recompute the ISO YYYY-MM-DD string whenever any of the three DOB selects
  // change — this is what we POST upstream.
  useEffect(() => {
    if (dobYear && dobMonth && dobDay) {
      setDob(`${dobYear}-${dobMonth}-${dobDay}`);
    } else {
      setDob("");
    }
  }, [dobYear, dobMonth, dobDay]);

  // Capture a still frame from the live <video> stream into an offscreen
  // canvas and store it as a JPEG data-URL. This is the ONLY route to a
  // selfie in this modal — there is no file input to fall back to.
  const captureFrame = () => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) {
      setCamError("The camera isn't ready yet — please wait a moment.");
      return;
    }
    const canvas = canvasRef.current || document.createElement("canvas");
    const w = video.videoWidth || 640;
    const h = video.videoHeight || 640;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, w, h);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
    setSelfie(dataUrl);
    setErr("");
    // Turn off the camera preview after the capture is stored. If the parent
    // wants to retake, we call startCamera() again below.
    stopCamera();
    setCamState("captured");
  };

  const retake = async () => {
    setSelfie("");
    await startCamera();
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!dob || !/^\d{4}-\d{2}-\d{2}$/.test(dob)) {
      setErr("Please choose your day, month and year of birth.");
      return;
    }
    if (!selfie) { setErr("Please capture a live selfie using the camera."); return; }
    setStatus("loading");
    try {
      // Strip the data-URL prefix; upstream stores just the base64 payload.
      const b64 = selfie.split(",", 2)[1] || selfie;
      await api.post("/auth/save-identity", { dob, selfie_photo_base64: b64 });
      await refresh();
      setStatus("success");
      setTimeout(() => {
        onOpenChange(false);
        onVerified && onVerified();
      }, 900);
    } catch (ex) {
      setStatus("error");
      setErr(formatApiError(ex) || "We couldn't verify that — please try again.");
    }
  };

  // Any state where the browser cannot deliver a live camera stream — used to
  // surface the "switch to your phone" helper. Kids get NO fallback path.
  const cameraUnavailable = camState === "blocked" || camState === "unsupported";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) stopCamera(); onOpenChange(o); }}>
      <DialogContent
        className="sm:max-w-md rounded-3xl border-0 p-0 overflow-hidden shadow-pop"
        data-testid="parent-verification-modal"
      >
        <div className="relative bg-gradient-to-br from-brandTeal to-brandTealDark px-6 pt-8 pb-6 text-white">
          <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-white/10" />
          <div className="absolute -left-4 -bottom-8 w-24 h-24 rounded-full bg-white/10" />
          <div className="relative flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] opacity-90">
            <ShieldCheck className="w-4 h-4" /> Family safety check
          </div>
          <DialogHeader className="relative mt-2 text-left space-y-1">
            <DialogTitle
              className="font-display text-2xl sm:text-3xl font-semibold text-white"
              data-testid="verification-modal-title"
            >
              Verify You&rsquo;re an Adult
            </DialogTitle>
            <DialogDescription
              className="text-white/90 text-sm leading-relaxed"
              data-testid="verification-modal-description"
            >
              To keep our parent community and Kid Zone safe, we verify that all account holders are real adults.
              For child safety, we require a <strong>live camera</strong> verification — uploads from your photo roll or hard drive are not accepted.
              {reason && <> {reason}</>}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 py-6 bg-white">
          {status === "success" ? (
            <div className="rounded-2xl bg-brandTeal/10 border border-brandTeal/30 p-4 flex items-start gap-3" data-testid="verification-success">
              <CheckCircle2 className="w-5 h-5 text-brandTeal flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-display text-sm font-semibold text-brandSlate">You&rsquo;re Adult Verified.</div>
                <div className="text-xs text-slate-600 mt-0.5">Thanks for keeping the marketplace safe. Continuing now…</div>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} noValidate data-testid="verification-form">
              <label className="block text-sm font-bold text-brandSlate mb-1">Date of birth</label>
              {/* Three-select DOB picker so parents don't have to scroll 40+
                  years back on a native mobile date input. Any modern browser
                  will still render the underlying keyboard-first select UX. */}
              <div className="grid grid-cols-3 gap-2 mb-4" data-testid="verification-dob-picker">
                <select
                  value={dobDay}
                  onChange={(e) => setDobDay(e.target.value)}
                  className="input-base"
                  data-testid="verification-dob-day"
                  disabled={status === "loading"}
                  aria-label="Day"
                >
                  <option value="">Day</option>
                  {Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0")).map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
                <select
                  value={dobMonth}
                  onChange={(e) => setDobMonth(e.target.value)}
                  className="input-base"
                  data-testid="verification-dob-month"
                  disabled={status === "loading"}
                  aria-label="Month"
                >
                  <option value="">Month</option>
                  {[
                    ["01", "January"], ["02", "February"], ["03", "March"], ["04", "April"],
                    ["05", "May"], ["06", "June"], ["07", "July"], ["08", "August"],
                    ["09", "September"], ["10", "October"], ["11", "November"], ["12", "December"],
                  ].map(([v, l]) => (<option key={v} value={v}>{l}</option>))}
                </select>
                <select
                  value={dobYear}
                  onChange={(e) => setDobYear(e.target.value)}
                  className="input-base"
                  data-testid="verification-dob-year"
                  disabled={status === "loading"}
                  aria-label="Year"
                >
                  <option value="">Year</option>
                  {(() => {
                    const now = new Date().getFullYear();
                    // 18+ minimum — cap the max year at (this year - 18).
                    const years = [];
                    for (let y = now - 18; y >= now - 100; y--) years.push(String(y));
                    return years.map((y) => (<option key={y} value={y}>{y}</option>));
                  })()}
                </select>
              </div>
              {/* Fallback native date input kept for auto-fill / accessibility
                  power users. Hidden by default but wired to the same state. */}
              <input
                type="date"
                max={new Date().toISOString().slice(0, 10)}
                value={dob}
                onChange={(e) => {
                  const v = e.target.value;
                  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
                    const [y, m, d] = v.split("-");
                    setDobYear(y); setDobMonth(m); setDobDay(d);
                  } else {
                    setDob(v);
                  }
                }}
                className="sr-only"
                tabIndex={-1}
                data-testid="verification-dob"
                disabled={status === "loading"}
                aria-hidden="true"
              />

              <label className="block text-sm font-bold text-brandSlate mb-1">Live selfie</label>

              {/* CAMERA STATES */}
              {cameraUnavailable ? (
                // Desktop-camera-denied helper. Kids on a school laptop with no
                // webcam land here — we route them to their phone/tablet.
                <div
                  className="rounded-2xl border-2 border-amber-200 bg-amber-50 p-4 flex items-start gap-3"
                  data-testid="verification-camera-blocked"
                >
                  <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 inline-flex items-center justify-center flex-shrink-0">
                    <Smartphone className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-display text-sm font-semibold text-amber-900">
                      Live camera required
                    </div>
                    <div className="text-xs text-amber-900/85 mt-1 leading-relaxed">
                      For child safety, a live camera verification is required. Please open{" "}
                      <strong>mytabletopapp.co.uk</strong> on your mobile phone or tablet to complete this step.
                    </div>
                    <button
                      type="button"
                      onClick={startCamera}
                      className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-amber-900 underline hover:no-underline"
                      data-testid="verification-camera-retry"
                    >
                      <RefreshCw className="w-3 h-3" /> Try camera again
                    </button>
                  </div>
                </div>
              ) : selfie ? (
                // A frame has been captured — preview it and offer a retake.
                <div className="rounded-2xl border border-slate-200 p-3 flex items-center gap-3" data-testid="verification-selfie-captured">
                  <img
                    src={selfie}
                    alt="Live selfie preview"
                    className="w-16 h-16 rounded-xl object-cover"
                    data-testid="verification-selfie-preview"
                  />
                  <div className="flex-1">
                    <div className="font-display text-sm font-semibold text-brandSlate">Live selfie captured</div>
                    <div className="text-xs text-slate-500 mt-0.5">Looks good? Continue below — or retake.</div>
                  </div>
                  <button
                    type="button"
                    onClick={retake}
                    className="text-xs font-bold text-brandTeal hover:text-brandTealDark inline-flex items-center gap-1"
                    data-testid="verification-selfie-retake"
                    disabled={status === "loading"}
                  >
                    <RefreshCw className="w-3 h-3" /> Retake
                  </button>
                </div>
              ) : (
                // LIVE CAMERA PREVIEW + capture button. This is the ONLY path
                // to a selfie in this modal.
                <div className="rounded-2xl border border-slate-200 overflow-hidden bg-slate-900" data-testid="verification-camera-live">
                  <div className="relative aspect-square bg-slate-900">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="absolute inset-0 w-full h-full object-cover"
                      // Mirror the preview so it feels like a mirror — matches
                      // the mobile app UX. The captured frame is NOT mirrored.
                      style={{ transform: "scaleX(-1)" }}
                      data-testid="verification-camera-video"
                    />
                    {camState !== "ready" && (
                      <div className="absolute inset-0 flex items-center justify-center text-white text-sm bg-slate-900/70" data-testid="verification-camera-status">
                        {camState === "requesting" ? (
                          <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Waiting for camera permission…</>
                        ) : (
                          <><Camera className="w-4 h-4 mr-2" /> Starting camera…</>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={captureFrame}
                    disabled={camState !== "ready" || status === "loading"}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-brandTeal text-white font-bold text-sm hover:bg-brandTealDark transition disabled:opacity-60"
                    data-testid="verification-capture-btn"
                  >
                    <Camera className="w-4 h-4" /> Capture live selfie
                  </button>
                </div>
              )}

              {/* Offscreen canvas used for frame capture — never visible. */}
              <canvas ref={canvasRef} style={{ display: "none" }} />

              {err && (
                <div className="mt-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3 flex items-start gap-2" data-testid="verification-error">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {err}
                </div>
              )}

              <button
                type="submit"
                disabled={status === "loading" || !selfie || cameraUnavailable}
                className="btn-teal w-full mt-5 disabled:opacity-60"
                data-testid="verification-submit-btn"
              >
                {status === "loading" ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Verifying…</>
                ) : (
                  <><ShieldCheck className="w-4 h-4" /> Verify me &amp; continue <ArrowRight className="w-4 h-4" /></>
                )}
              </button>

              <div className="mt-4 text-[11px] text-slate-500 leading-relaxed">
                We use this only to confirm you&rsquo;re an adult and to protect other families.
                Your selfie is captured live in your browser — no photo-roll uploads accepted.
              </div>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
